import type { PublicRecipe } from '@recipes/contracts';
import { formatIngredientLine, formatMinutes } from '@recipes/domain';
import { Alert, Badge, Button, Spinner } from '@recipes/ui';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Printer } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { allergenLabel, dietaryLabel } from '@/features/shared/labels';
import { api } from '@/lib/api';
import { overrideAppearance } from '@/lib/appearance/appearance';
import { useAuth } from '@/lib/auth/auth-provider';
import { describeError } from '@/lib/errors';
import { currentLocale } from '@/lib/i18n';
import { queryKeys } from '@/lib/query-keys';

const PRINT_CSS = `
@media print {
  nav, .print-hidden { display: none !important; }
  body { background: var(--color-paper); color: var(--color-ink); }
  main { padding: 0 !important; }
  .print-page { max-width: none !important; padding: 0 !important; }
  a { color: inherit; text-decoration: none; }
  img { max-height: 60mm; }
  section { break-inside: avoid; }
}
`;

/** Read-only page for UNLISTED/PUBLIC recipes (guests allowed; the shell hides nav for them). */
export function PublicRecipePage() {
  const { slug = '' } = useParams();
  const { t } = useTranslation();
  const { status } = useAuth();
  const locale = currentLocale();
  const query = useQuery({
    queryKey: queryKeys.recipes.public(slug),
    queryFn: () => api.recipes.public(slug),
    enabled: Boolean(slug),
  });
  const recipe = query.data;

  // A shared page is not the visitor's own app: the installation's theme, the system's mode.
  useEffect(() => {
    overrideAppearance(true);
    return () => overrideAppearance(false);
  }, []);

  useEffect(() => {
    const previous = document.title;
    if (recipe) document.title = `${recipe.title} · ${t('app.name')}`;
    return () => {
      document.title = previous;
    };
  }, [recipe, t]);

  if (query.isLoading)
    return (
      <div className="p-6">
        <Spinner label={t('app.loading')} />
      </div>
    );
  if (!recipe)
    return (
      <div className="mx-auto max-w-lg p-6">
        <Alert variant="destructive" title={t('public.notFoundTitle')}>
          {describeError(query.error)}
        </Alert>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/">{t('public.goHome')}</Link>
        </Button>
      </div>
    );

  return (
    <article className="print-page mx-auto w-full max-w-3xl p-4 md:p-6" data-testid="public-recipe">
      <style>{PRINT_CSS}</style>
      <div className="print-hidden mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-muted/40 p-3 text-sm">
        <span>{status === 'authenticated' ? t('public.ctaLoggedIn') : t('public.ctaGuest')}</span>
        <span className="flex gap-2">
          {status === 'authenticated' ? (
            <Button asChild size="sm">
              <Link to="/">{t('public.openApp')}</Link>
            </Button>
          ) : (
            <>
              <Button asChild size="sm">
                <Link to="/register">{t('nav.register')}</Link>
              </Button>
              <Button asChild size="sm" variant="outline">
                <Link to="/login">{t('nav.login')}</Link>
              </Button>
            </>
          )}
          <Button size="sm" variant="outline" onClick={() => window.print()}>
            <Printer aria-hidden /> {t('public.print')}
          </Button>
        </span>
      </div>

      <header className="mb-6">
        <h1 className="text-3xl font-bold leading-tight" lang={recipe.language ?? undefined}>
          {recipe.title}
        </h1>
        {recipe.description && <p className="mt-2 text-muted-foreground">{recipe.description}</p>}
        <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <div>
            <dt className="inline text-muted-foreground">{t('common.servings')}: </dt>
            <dd className="inline font-medium">
              {recipe.servings} {recipe.servingsLabel ?? ''}
            </dd>
          </div>
          {recipe.totalTimeMinutes != null && (
            <div>
              <dt className="inline text-muted-foreground">{t('public.totalTime')}: </dt>
              <dd className="inline font-medium">
                {formatMinutes(recipe.totalTimeMinutes, locale)}
              </dd>
            </div>
          )}
          {recipe.prepTimeMinutes != null && (
            <div>
              <dt className="inline text-muted-foreground">{t('review.fields.prepTime')}: </dt>
              <dd className="inline font-medium">
                {formatMinutes(recipe.prepTimeMinutes, locale)}
              </dd>
            </div>
          )}
          {recipe.cookTimeMinutes != null && (
            <div>
              <dt className="inline text-muted-foreground">{t('review.fields.cookTime')}: </dt>
              <dd className="inline font-medium">
                {formatMinutes(recipe.cookTimeMinutes, locale)}
              </dd>
            </div>
          )}
        </dl>
        <p className="mt-2 text-xs text-muted-foreground">
          {t('public.by', { name: recipe.owner.displayName })}
        </p>
      </header>

      <SourceAttribution recipe={recipe} />

      {recipe.cover && (
        <img
          src={recipe.cover.url}
          alt={recipe.cover.altText ?? recipe.title}
          className="mb-6 max-h-96 w-full rounded-lg object-cover"
        />
      )}

      {recipe.labels.length > 0 && (
        <ul className="mb-6 flex flex-wrap gap-2" aria-label={t('review.sections.labels')}>
          {recipe.labels.map((l) => (
            <li key={l.id}>
              <Badge variant={l.confirmed ? 'secondary' : 'warning'}>
                {l.kind === 'DIETARY' ? dietaryLabel(t, l.value) : allergenLabel(t, l.value)}
                {!l.confirmed && ` · ${t('review.suggestedVerify')}`}
              </Badge>
            </li>
          ))}
        </ul>
      )}

      <div className="grid gap-8 md:grid-cols-[minmax(0,18rem)_1fr]">
        <section aria-labelledby="pub-ingredients">
          <h2 id="pub-ingredients" className="mb-2 text-xl font-semibold">
            {t('review.sections.ingredients')}
          </h2>
          {recipe.ingredientSections.map((section) => (
            <div key={section.id} className="mb-3">
              {section.title && (
                <h3 className="mb-1 text-sm font-semibold text-muted-foreground">
                  {section.title}
                </h3>
              )}
              <ul className="space-y-1 text-sm">
                {section.ingredients.map((ing) => (
                  <li key={ing.id}>{formatIngredientLine(ing, locale).line}</li>
                ))}
              </ul>
            </div>
          ))}
        </section>
        <section aria-labelledby="pub-steps">
          <h2 id="pub-steps" className="mb-2 text-xl font-semibold">
            {t('review.sections.steps')}
          </h2>
          {recipe.stepSections.map((section) => (
            <div key={section.id} className="mb-3">
              {section.title && (
                <h3 className="mb-1 text-sm font-semibold text-muted-foreground">
                  {section.title}
                </h3>
              )}
              <ol
                className="list-decimal space-y-3 pl-5 text-sm leading-relaxed"
                lang={recipe.language ?? undefined}
              >
                {section.steps.map((step) => (
                  <li key={step.id}>
                    {step.instruction}
                    {step.durationSeconds ? (
                      <span className="ml-1 text-muted-foreground">
                        ({formatMinutes(Math.round(step.durationSeconds / 60), locale)})
                      </span>
                    ) : null}
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </section>
      </div>
    </article>
  );
}

function SourceAttribution({ recipe }: { recipe: PublicRecipe }) {
  const { t } = useTranslation();
  const source = recipe.source;
  if (!source && recipe.origin === 'USER') return null;
  const url = source?.canonicalUrl ?? source?.url ?? null;
  return (
    <section
      aria-label={t('imports.source.title')}
      className="mb-6 rounded-lg border border-info/50 bg-info/5 p-3 text-sm"
      data-testid="public-source"
    >
      <p className="font-medium">{t('imports.source.title')}</p>
      {recipe.origin === 'AI_GENERATED' && <p>{t('review.badgeAi')}</p>}
      {source?.siteName || source?.domain ? <p>{source.siteName ?? source.domain}</p> : null}
      {source?.author && (
        <p>
          {t('imports.source.author')}: {source.author}
        </p>
      )}
      {source?.originalTitle && (
        <p>
          {t('imports.source.originalTitle')}: {source.originalTitle}
        </p>
      )}
      {url && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="inline-flex items-center gap-1 break-all text-primary underline"
        >
          {url} <ExternalLink className="size-3.5" aria-hidden />
        </a>
      )}
      {source?.attribution && <p className="text-muted-foreground">{source.attribution}</p>}
      <p className="mt-1 text-xs text-muted-foreground">{t('imports.source.notOurs')}</p>
    </section>
  );
}
